import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconModule } from 'src/components/icon/icon.module';

type CalendarDateDay = {
  active: 0 | 1;
  date: Date;
  label: number;
};

@Component({
  selector: 'app-datepicker-default',
  templateUrl: './datepicker-default.component.html',
  styleUrls: ['./datepicker-default.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IconModule],
})
export class DatepickerDefaultComponent implements OnInit {
  @Input()
  selectMode: 'single' | 'range' = 'range';

  @Input()
  DateTemplate?: TemplateRef<any>;

  @Input()
  set startDate(value: Date | undefined) {
    this.rangeStart = value;
  }

  @Input()
  set endDate(value: Date | undefined) {
    this.rangeEnd = value;
  }

  @Input()
  set annotations(
    value: {
      day: number;
      month: number;
      year: number;
    }[],
  ) {
    this._annotations = value.map((annotation) =>
      new Date(annotation.year, annotation.month, annotation.day).getTime(),
    );
  }

  @Input()
  minDate?: Date;

  @Input()
  maxDate?: Date;

  _annotations: number[] = [];

  @Output()
  close = new EventEmitter<void>();

  @Output()
  endDateChange = new EventEmitter<Date | undefined>();

  @Output()
  startDateChange = new EventEmitter<Date | undefined>();

  dateMode: 'date' | 'month' | 'year' = 'date';

  months = [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember',
  ];

  days = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

  today = new Date().setHours(0, 0, 0, 0);

  activeMonth = new Date().getMonth();
  activeYear = new Date().getFullYear();

  calendarDate: CalendarDateDay[][] = [];

  rangeStart?: Date;
  rangeEnd?: Date;

  yearRangeStart = 1;
  yearRangeEnd = 9;
  yearOptions: number[] = Array(12);

  constructor() {}

  ngOnInit() {
    if (this.rangeStart) {
      this.activeMonth = this.rangeStart.getMonth();
      this.activeYear = this.rangeStart.getFullYear();
    }

    this.__generateCalendarDate();
  }

  changeMode(mode: 'date' | 'month' | 'year') {
    this.dateMode = mode;

    if (mode === 'year') {
      this.__generateRangeYear(this.activeYear);
    }
  }

  selectDate(date: CalendarDateDay) {
    if (this.selectMode === 'single') {
      this.rangeStart = date.date;
      this.rangeEnd = date.date;

      this.emitChange();
      return;
    }

    if (this.selectMode === 'range' && this.rangeStart && !this.rangeEnd) {
      // if (date.date.getTime() === this.rangeStart.getTime()) {
      //   this.rangeStart = date.date;
      //   this.rangeEnd = undefined;
      //   this.emitChange();
      //   return;
      // }

      // Handle if user select date start from the newer date and then select the older date
      if (date.date.getTime() < (this.rangeStart!.getTime() ?? 0)) {
        const tempRangeStart = this.rangeStart;
        this.rangeStart = date.date;
        this.rangeEnd = tempRangeStart;
        this.emitChange();
        return;
      }

      this.rangeEnd = date.date;
      this.emitChange();
    } else {
      this.rangeStart = date.date;
      this.rangeEnd = undefined;
      this.startDateChange.emit(this.rangeStart);
    }
  }

  resetSelectedDate() {
    this.rangeStart = undefined;
    this.rangeEnd = undefined;
    this.emitChange();
  }

  emitChange() {
    this.startDateChange.emit(this.rangeStart);
    this.endDateChange.emit(this.rangeEnd);
  }

  prevMonth() {
    if (this.activeMonth === 0) {
      this.activeMonth = 11;
      this.activeYear -= 1;
    } else {
      this.activeMonth -= 1;
    }

    this.__generateCalendarDate();
  }

  nextMonth() {
    if (this.activeMonth === 11) {
      this.activeMonth = 0;
      this.activeYear += 1;
    } else {
      this.activeMonth += 1;
    }

    this.__generateCalendarDate();
  }

  selectMonth(idxMonth: number) {
    this.activeMonth = idxMonth;
    this.changeMode('date');
    this.__generateCalendarDate();
  }

  prevYear() {
    this.activeYear--;
  }

  nextYear() {
    this.activeYear++;
  }

  prevRangeYear() {
    this.__generateRangeYear(this.yearRangeStart - 1);
  }

  nextRangeYear() {
    this.__generateRangeYear(this.yearRangeEnd + 1);
  }

  selectYear(year: number) {
    this.activeYear = year;
    this.changeMode('month');
  }

  private __generateCalendarDate() {
    this.calendarDate = [];

    const firstDayOfMonth = new Date(this.activeYear, this.activeMonth, 1);
    const firstDayInDay = firstDayOfMonth.getDay();

    const lastDayOfMonth = new Date(this.activeYear, this.activeMonth + 1, 0);
    const lastDayInDate = lastDayOfMonth.getDate();

    const lastDayOfPrevMonth = new Date(this.activeYear, this.activeMonth, 0);

    let row = 0;
    let dayInWeek = firstDayInDay;
    for (let i = 1; i <= 6 * 7 - firstDayInDay; i++) {
      if (!this.calendarDate[row]) {
        this.calendarDate[row] = Array(7);
      }

      if (i > lastDayInDate) {
        const nextYear = this.activeMonth === 11 ? this.activeYear + 1 : this.activeYear;
        const nextMonth = this.activeMonth === 11 ? 0 : this.activeMonth + 1;
        this.calendarDate[row][dayInWeek] = {
          active: 0,
          date: new Date(nextYear, nextMonth, i - lastDayInDate),
          label: i - lastDayInDate,
        };
      } else {
        if (
          this.maxDate &&
          new Date(this.activeYear, this.activeMonth, i).getTime() > this.maxDate.getTime()
        ) {
          // Handle if maxDate is defined
          this.calendarDate[row][dayInWeek] = {
            active: 0,
            date: new Date(this.activeYear, this.activeMonth, i),
            label: i,
          };
        } else if (
          this.minDate &&
          new Date(this.activeYear, this.activeMonth, i).getTime() < this.minDate.getTime()
        ) {
          // Handle if minDate is defined
          this.calendarDate[row][dayInWeek] = {
            active: 0,
            date: new Date(this.activeYear, this.activeMonth, i),
            label: i,
          };
        } else {
          this.calendarDate[row][dayInWeek] = {
            active: 1,
            date: new Date(this.activeYear, this.activeMonth, i),
            label: i,
          };
        }
      }

      dayInWeek++;

      if (dayInWeek >= 7) {
        row++;
        dayInWeek = 0;
      }
    }

    const idxFirstFillRow = this.calendarDate[0].findIndex((value) => Boolean(value));
    for (let i = idxFirstFillRow; i > 0; i--) {
      const prevYear = this.activeMonth === 0 ? this.activeYear - 1 : this.activeYear;
      const prevMonth = this.activeMonth === 0 ? 11 : this.activeMonth - 1;

      this.calendarDate[0][idxFirstFillRow - i] = {
        active: 0,
        date: new Date(prevYear, prevMonth, lastDayOfPrevMonth.getDate() - i + 1),
        label: lastDayOfPrevMonth.getDate() - i + 1,
      };
    }
  }

  private __generateRangeYear(inRangeYear: number) {
    this.yearRangeStart = Math.floor(inRangeYear / 10) * 10;
    this.yearRangeEnd = this.yearRangeStart + 9;
    this.yearOptions = Array(12)
      .fill(0)
      .map((_, index) => this.yearRangeStart - 1 + index);
  }
}
